import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import api, { formatError } from "@/lib/api";
import { trackRegister } from "@/lib/analytics";
import { HardHat, Building2, Users2, Handshake, ArrowRight, ArrowLeft, Eye, EyeOff, Phone, Mail, CheckCircle2 } from "lucide-react";
import GoogleSignInButton from "@/components/GoogleSignInButton";

const EXP_OPTIONS = [0, 1, 2, 3, 5, 10];

// Roles configured as tiles. "hr" is treated as a recruiter variant.
const ROLE_TILES = [
  {
    key: "worker",
    title: "Worker",
    subtitle: "Find frontline jobs across India",
    Icon: HardHat,
    bg: "bg-sarthi-primary",
    shadow: "shadow-[0_5px_0_0_#B84A25]",
  },
  {
    key: "contractor",
    title: "Business Partner",
    subtitle: "Post jobs & hire skilled workers",
    Icon: Building2,
    bg: "bg-sarthi-secondary",
    shadow: "shadow-[0_5px_0_0_#1F3A64]",
  },
  {
    key: "hr",
    title: "HR / Recruiter",
    subtitle: "Manage pipeline & hire faster",
    Icon: Users2,
    bg: "bg-emerald-600",
    shadow: "shadow-[0_5px_0_0_#065F46]",
  },
  {
    key: "agency",
    title: "Agency Partner",
    subtitle: "Multi-vendor: manage a candidate pool",
    Icon: Handshake,
    bg: "bg-indigo-600",
    shadow: "shadow-[0_5px_0_0_#3730A3]",
  },
];

export default function Register() {
  const { t } = useI18n();
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const initialRole = ["worker", "contractor", "hr", "agency"].includes(sp.get("role")) ? sp.get("role") : null;

  const [step, setStep] = useState(initialRole ? 1 : 0);
  const [role, setRole] = useState(initialRole || "worker");
  const [cats, setCats] = useState([]);
  const [primarySkill, setPrimarySkill] = useState("");
  const [exp, setExp] = useState(null);
  const [authMode, setAuthMode] = useState("email"); // email | mobile
  const [form, setForm] = useState({
    name: "", email: "", password: "", mobile: "",
    location: "", company_name: "", gst: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data || [])).catch(() => {}); }, []);

  const totalSteps = role === "worker" ? 3 : 2;
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submitEmail = async () => {
    if (!form.name.trim()) return toast.error("Please enter your name");
    if (!form.email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return toast.error("Please enter a valid email");
    if (form.password.length < 6) return toast.error("Password must be 6+ characters");
    if (!acceptTerms) return toast.error("Please accept the Terms of Service and Privacy Policy to continue");
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        role,
        mobile: form.mobile || undefined,
        location: form.location || undefined,
        skills: role === "worker" && primarySkill ? [primarySkill] : [],
        experience_years: role === "worker" ? Number(exp) : 0,
        company_name: (role === "contractor" || role === "hr") ? form.company_name : undefined,
        gst: (role === "contractor" || role === "hr") ? form.gst : undefined,
      };
      const { data } = await api.post("/auth/register", payload);
      login(data);
      trackRegister(role);
      toast.success("Welcome to Sarthi!");
      nav("/dashboard");
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const sendMobileOtp = async () => {
    if (!/^\d{10,15}$/.test(form.mobile)) return toast.error("Enter a valid 10-digit mobile number");
    if (!form.name.trim()) return toast.error("Please enter your name");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/send", { mobile: form.mobile });
      setOtpSent(true);
      if (data?.via === "sms") {
        toast.success("OTP sent to your mobile");
      } else if (data?.fallback_reason === "twilio_daily_limit") {
        toast.error("SMS quota reached for today. Please use Email + Password or try again after 12 AM.", { duration: 8000 });
      } else if (data?.demo_otp) {
        toast.info(`SMS unavailable — dev OTP: ${data.demo_otp}`, { duration: 15000 });
      } else {
        toast.success("OTP sent");
      }
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const verifyMobileOtp = async () => {
    if (otp.length !== 6) return toast.error("Enter the 6-digit OTP");
    if (!acceptTerms) return toast.error("Please accept the Terms of Service and Privacy Policy to continue");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/verify", {
        mobile: form.mobile,
        otp,
        role,
        name: form.name.trim(),
        skills: role === "worker" && primarySkill ? [primarySkill] : [],
        experience_years: role === "worker" ? Number(exp) : 0,
        location: form.location || undefined,
      });
      login(data);
      trackRegister(role);
      toast.success("Welcome to Sarthi!");
      nav("/dashboard");
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-slate-50 via-white to-sarthi-accent/40 px-4 py-10 sm:px-6">
      <div className="mx-auto max-w-2xl">
        {/* Centered card wizard */}
        <div className="sarthi-card p-6 sm:p-8" data-testid="register-card">
          {/* Header + stepper */}
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <h1 className="font-display text-2xl font-extrabold text-sarthi-text sm:text-3xl">
                {step === 0 ? "Create your account" : role === "worker" ? "Candidate registration" : role === "contractor" ? "Business Partner sign-up" : role === "agency" ? "Agency Partner sign-up" : "HR / Recruiter sign-up"}
              </h1>
              <p className="mt-1 text-sm font-medium text-sarthi-muted">Free forever · verified recruiters · India-wide jobs</p>
            </div>
            <div
              data-testid="wizard-step-indicator"
              className="flex-none rounded-full border-2 border-sarthi-border bg-white px-3 py-1 text-xs font-extrabold text-sarthi-secondary"
            >
              Step {Math.min(step + 1, totalSteps)} of {totalSteps}
            </div>
          </div>

          <StepperBar step={step} total={totalSteps} />

          {/* Step 0: Role select */}
          {step === 0 && (
            <>
              <div className="mt-6">
                <GoogleSignInButton testid="register-google-btn" label="Continue with Google" />
                <div className="my-4 flex items-center gap-3 text-xs font-bold uppercase tracking-widest text-sarthi-muted">
                  <div className="h-px flex-1 bg-sarthi-border" /> or pick a role <div className="h-px flex-1 bg-sarthi-border" />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {ROLE_TILES.map((r) => {
                const Icon = r.Icon;
                return (
                  <button
                    key={r.key}
                    data-testid={`pick-${r.key}`}
                    onClick={() => { setRole(r.key); setStep(1); }}
                    className="sarthi-card sarthi-card-press flex flex-col items-center gap-3 p-6 text-center transition-all hover:scale-[1.02]"
                  >
                    <div className={`flex h-16 w-16 items-center justify-center rounded-2xl ${r.bg} text-white ${r.shadow}`}>
                      <Icon className="h-8 w-8" strokeWidth={2.5} />
                    </div>
                    <div className="font-display text-lg font-extrabold">{r.title}</div>
                    <div className="text-xs font-medium text-sarthi-muted">{r.subtitle}</div>
                  </button>
                );
              })}
            </div>
            </>
          )}

          {/* Step 1: Worker trade + experience */}
          {step === 1 && role === "worker" && (
            <div className="mt-6 space-y-5">
              <div>
                <label className="mb-1 block text-sm font-bold uppercase tracking-wide text-sarthi-muted">Select your trade *</label>
                <select
                  data-testid="trade-select"
                  value={primarySkill}
                  onChange={(e) => setPrimarySkill(e.target.value)}
                  className="w-full rounded-xl border-2 border-sarthi-border bg-white px-4 py-3 text-base font-semibold focus:border-sarthi-primary focus:outline-none"
                >
                  <option value="">-- Choose your trade --</option>
                  {cats.map((c) => (
                    <option key={c.key} value={c.key}>{c.name_en || c.name || c.key}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-bold uppercase tracking-wide text-sarthi-muted">Experience <span className="text-sarthi-primary">*</span></label>
                <div className="flex flex-wrap gap-2">
                  {EXP_OPTIONS.map((y) => (
                    <button
                      key={y}
                      data-testid={`exp-${y}`}
                      onClick={() => setExp(y)}
                      className={`rounded-xl border-2 px-4 py-2 text-sm font-bold ${exp === y ? "border-sarthi-primary bg-sarthi-accent text-sarthi-primary" : "border-sarthi-border bg-white text-sarthi-text"}`}
                    >
                      {y}{y === 10 ? "+" : ""} yr
                    </button>
                  ))}
                </div>
                {exp === null && (
                  <p className="mt-1.5 text-xs font-semibold text-sarthi-muted">Tap your years of experience to continue</p>
                )}
              </div>
              <NavRow onBack={() => setStep(0)} onNext={() => setStep(2)} nextDisabled={!primarySkill || exp === null || exp === undefined} />
            </div>
          )}

          {/* Step details: contractor/hr/agency basics OR worker basics */}
          {((step === 2 && role === "worker") || (step === 1 && (role === "contractor" || role === "hr" || role === "agency"))) && (
            <div className="mt-6 space-y-5">
              {/* Auth mode toggle */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  data-testid="auth-mode-email"
                  onClick={() => { setAuthMode("email"); setOtpSent(false); }}
                  className={`flex items-center justify-center gap-2 rounded-xl border-2 py-3 text-sm font-bold ${authMode === "email" ? "border-sarthi-primary bg-sarthi-accent text-sarthi-primary" : "border-sarthi-border bg-white"}`}
                >
                  <Mail className="h-4 w-4" /> Email + Password
                </button>
                <button
                  data-testid="auth-mode-mobile"
                  onClick={() => { setAuthMode("mobile"); setOtpSent(false); }}
                  className={`flex items-center justify-center gap-2 rounded-xl border-2 py-3 text-sm font-bold ${authMode === "mobile" ? "border-sarthi-primary bg-sarthi-accent text-sarthi-primary" : "border-sarthi-border bg-white"}`}
                >
                  <Phone className="h-4 w-4" /> Mobile OTP
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field testid="reg-name" label="Full name *" value={form.name} onChange={set("name")} />
                <Field testid="reg-location" label="Location" value={form.location} onChange={set("location")} placeholder="City, State" />
                {(role === "contractor" || role === "hr" || role === "agency") && (
                  <>
                    <Field testid="reg-company" label={role === "agency" ? "Agency / Company name *" : "Company name"} value={form.company_name} onChange={set("company_name")} />
                    <Field testid="reg-gst" label={role === "agency" ? "GSTIN (optional)" : "GST (optional)"} value={form.gst} onChange={set("gst")} />
                  </>
                )}
                {authMode === "email" ? (
                  <>
                    <Field testid="reg-email" label="Email *" type="email" value={form.email} onChange={set("email")} />
                    <div>
                      <label className="mb-1 block text-sm font-bold uppercase tracking-wide text-sarthi-muted">Password *</label>
                      <div className="relative">
                        <input
                          data-testid="reg-password"
                          type={showPw ? "text" : "password"}
                          value={form.password}
                          onChange={set("password")}
                          className="w-full rounded-xl border-2 border-sarthi-border bg-white px-4 py-3 pr-12 text-base font-semibold focus:border-sarthi-primary focus:outline-none"
                          placeholder="Min 6 characters"
                        />
                        <button
                          type="button"
                          data-testid="reg-toggle-password"
                          onClick={() => setShowPw((v) => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-sarthi-muted hover:text-sarthi-primary"
                          aria-label={showPw ? "Hide password" : "Show password"}
                        >
                          {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>
                    <Field testid="reg-mobile" label="Mobile (optional)" value={form.mobile} onChange={set("mobile")} inputMode="numeric" />
                  </>
                ) : (
                  <>
                    <Field testid="reg-mobile-primary" label="Mobile number *" value={form.mobile} onChange={set("mobile")} inputMode="numeric" placeholder="10-digit mobile" />
                    <Field testid="reg-email-optional" label="Email (optional)" type="email" value={form.email} onChange={set("email")} />
                  </>
                )}
              </div>

              {/* Mobile OTP flow */}
              {authMode === "mobile" && otpSent && (
                <div>
                  <label className="mb-1 block text-sm font-bold uppercase tracking-wide text-sarthi-muted">Enter OTP</label>
                  <input
                    data-testid="reg-otp"
                    inputMode="numeric"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    className="w-full rounded-xl border-2 border-sarthi-border bg-white px-4 py-4 text-center text-2xl font-bold tracking-[0.5em] focus:border-sarthi-primary focus:outline-none"
                    placeholder="______"
                  />
                  <div className="mt-2 flex items-center justify-between text-xs font-semibold">
                    <span className="text-sarthi-muted">Didn't receive?</span>
                    <button type="button" onClick={sendMobileOtp} disabled={busy} className="font-bold text-sarthi-primary hover:underline disabled:opacity-50" data-testid="reg-resend-otp">
                      Resend OTP
                    </button>
                  </div>
                </div>
              )}

              <label className="flex items-start gap-2 rounded-xl border-2 border-sarthi-border bg-sarthi-accent/20 p-3 text-xs font-semibold text-sarthi-text">
                <input
                  data-testid="accept-terms-checkbox"
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="mt-0.5 h-4 w-4 flex-none accent-sarthi-primary"
                />
                <span>
                  I have read and agree to the{" "}
                  <Link to="/terms" target="_blank" className="font-bold text-sarthi-primary underline">Terms of Service</Link>,{" "}
                  <Link to="/privacy" target="_blank" className="font-bold text-sarthi-primary underline">Privacy Policy</Link> and{" "}
                  <Link to="/disclaimer" target="_blank" className="font-bold text-sarthi-primary underline">Disclaimer</Link>.
                  I confirm I am 18 years or older.
                </span>
              </label>

              <div className="flex justify-between gap-3 pt-2">
                <button onClick={() => setStep(role === "worker" ? 1 : 0)} className="sarthi-btn-outline inline-flex items-center gap-2 px-5 py-3 font-bold">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                {authMode === "email" ? (
                  <button data-testid="register-submit" onClick={submitEmail} disabled={busy || !acceptTerms} className="sarthi-btn-primary inline-flex items-center gap-2 px-6 py-3 text-white disabled:opacity-50">
                    {busy ? "Creating…" : "Create account"} <ArrowRight className="h-4 w-4" />
                  </button>
                ) : !otpSent ? (
                  <button data-testid="reg-send-otp" onClick={sendMobileOtp} disabled={busy || !acceptTerms} className="sarthi-btn-primary inline-flex items-center gap-2 px-6 py-3 text-white disabled:opacity-50">
                    {busy ? "Sending…" : "Send OTP"} <ArrowRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button data-testid="reg-verify-otp" onClick={verifyMobileOtp} disabled={busy || otp.length !== 6 || !acceptTerms} className="sarthi-btn-primary inline-flex items-center gap-2 px-6 py-3 text-white disabled:opacity-50">
                    {busy ? "Verifying…" : "Verify & Continue"} <ArrowRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          )}

          <TrustBar />
        </div>

        <div className="mt-6 text-center text-sm font-semibold text-sarthi-muted">
          Already have an account? <Link to="/login" data-testid="go-login" className="font-bold text-sarthi-primary underline">Sign in</Link>
        </div>
      </div>
    </div>
  );
}

function StepperBar({ step, total }) {
  return (
    <div className="mt-2 flex items-center gap-2" data-testid="wizard-stepper">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1.5 flex-1 rounded-full transition-all ${i <= step ? "bg-sarthi-primary" : "bg-sarthi-border"}`}
        />
      ))}
    </div>
  );
}

function NavRow({ onBack, onNext, nextDisabled }) {
  return (
    <div className="flex justify-between gap-3 pt-2">
      <button onClick={onBack} className="sarthi-btn-outline inline-flex items-center gap-2 px-5 py-3 font-bold">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <button
        data-testid="wizard-next"
        onClick={onNext}
        disabled={nextDisabled}
        className="sarthi-btn-primary inline-flex items-center gap-2 px-6 py-3 text-white disabled:opacity-50"
      >
        Next <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}

function TrustBar() {
  const bullets = useMemo(() => ([
    "100% free for workers",
    "Verified recruiters only",
    "Real jobs · Live SMS OTP",
  ]), []);
  return (
    <div className="mt-6 flex flex-wrap items-center gap-4 border-t border-sarthi-border/60 pt-4 text-xs font-semibold text-sarthi-muted">
      {bullets.map((b) => (
        <span key={b} className="inline-flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> {b}
        </span>
      ))}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", inputMode, testid, placeholder }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold uppercase tracking-wide text-sarthi-muted">{label}</label>
      <input
        data-testid={testid}
        type={type}
        inputMode={inputMode}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-xl border-2 border-sarthi-border bg-white px-4 py-3 text-base font-semibold focus:border-sarthi-primary focus:outline-none"
      />
    </div>
  );
}
